import React, { useState, useEffect } from "react";
import { useForm, Controller } from "react-hook-form";
import TagsInput from "../TagsInput/TagsInput";
import Rating from "react-rating";
import styles from "./CreateListingForm.module.css";
import { useNavigate } from "react-router-dom";
import { BASE_URL } from "../../App";
import RoommateSelector from "../RoommateSelector/RoommateSelector";

export default function CreateListingPage() {
    const {
        register,
        handleSubmit,
        control,
        clearErrors,
        formState: { errors, submitCount },
    } = useForm({
        defaultValues: { landlord_rating: 0 },
        mode: "onSubmit",
    });

    // UPDATED: Clear field-level validation errors after 5 seconds (was 3000)
    useEffect(() => {
        if (Object.keys(errors).length > 0) {
            const timer = setTimeout(() => {
                clearErrors();
            }, 5000);
            return () => clearTimeout(timer);
        }
    }, [submitCount, errors, clearErrors]);

    const navigate = useNavigate();

    const [selectedImages, setSelectedImages] = useState([]);
    const handleFileChange = (e) => {
        const files = Array.from(e.target.files || []);
        if (files.length > 0) {
            // Append to existing list instead of overwriting
            setSelectedImages((prev) => [...prev, ...files]);
        }

        // Clear the input so the same file can be re-selected later if needed
        e.target.value = "";
    };

    const nonNegativeRule = {
        validate: (value) =>
            Number(value) >= 0 || "Numerical inputs cannot be negative",
    };

    // 🔔 Same pattern as SettingsPage: flash banner (success/error)
    const [flash, setFlash] = useState({ type: "", text: "" });
    const showFlash = (text, type = "error") => setFlash({ type, text });

    // NEW: useEffect hook to dismiss flash error messages after 5 seconds
    useEffect(() => {
        if (flash.text && flash.type === "error") {
            const timer = setTimeout(() => {
                setFlash({ type: "", text: "" }); // Clear the message
            }, 5000); 

            // Cleanup function to clear the timeout if the component unmounts or flash changes
            return () => clearTimeout(timer);
        }
    }, [flash]);

    const onError = () => {
        showFlash("Please fill out all required fields", "error");
    };

    // Prevent typing "-" or "e" in numeric inputs
    const preventInvalidNumericInput = (e) => {
        if (["-", "e", "E", "+"].includes(e.key)) {
            e.preventDefault();
        }
    };

    async function onSubmit(data) {
        const formData = new FormData();
        // Require at least one uploaded photo
        if (selectedImages.length === 0) {
            showFlash("Please upload at least one photo before submitting.", "error");
            return;
        }
        const address =
            data.addressLine1 + (data.addressLine2 ? " " + data.addressLine2 : "");
        formData.append("address", address);

        for (const key in data) {
            if (key === "addressLine1" || key === "addressLine2" || key === "images") continue;

            if (key === "roommates") {
                if (Array.isArray(data.roommates)) {
                    data.roommates.forEach((r) => {
                        if (r && typeof r === "object" && "id" in r) {
                            formData.append("roommates[]", `${r.name}|${r.id}`);
                        } else if (typeof r === "string") {
                            formData.append("roommates[]", r.trim());
                        }
                    });
                }
            } else if (key === "utilities" || key === "tags") {
                if (Array.isArray(data[key])) {
                    data[key].forEach((val) => {
                        formData.append(`${key}[]`, val);
                    });
                }
            } else {
                formData.append(key, data[key]);
            }
        }

        // ✅ Append files after collecting the rest of the data
        selectedImages.forEach((file) => {
            formData.append("images[]", file);
        });

        try {
            const res = await fetch(`${BASE_URL}backend/create_listing.php`, {
                method: "POST",
                body: formData,
            });

            // Try JSON first, fall back to raw text
            const ct = res.headers.get("content-type") || "";
            const payload = ct.includes("application/json")
                ? await res.json()
                : { raw: await res.text() };

            if (res.ok && (payload?.success ?? true)) {
                setFlash({ type: "success", text: "Listing created successfully!" });
                // small delay so users can see the banner (optional)
                setTimeout(() => navigate("/home"), 400);
            } else {
                showFlash(
                    `Error: ${payload?.error || payload?.message || payload?.raw || "Could not create listing."}`,
                    "error"
                );
            }
        } catch (e) {
            showFlash("Network error. Please try again.", "error");
        }
    }

    return (
        <form
            onSubmit={handleSubmit(onSubmit, onError)}
            className={styles.component}
            noValidate
        >
            <div className="input-group">
                <label htmlFor="title">Title</label>
                <input
                    id="title"
                    {...register("title", {
                        required: "Title is required",
                        minLength: { value: 3, message: "Title must be at least 3 characters" },
                        maxLength: { value: 100, message: "Title cannot exceed 100 characters" },
                    })}
                    placeholder="Title"
                    aria-invalid={!!errors.title}
                />
                {errors.title && <p className={styles.fieldError}>{errors.title.message}</p>}
            </div>

            <div className="input-group">
                <label htmlFor="rent">Rent</label>
                <input
                    type="number"
                    id="rent"
                    {...register("rent", {
                        required: "Rent is required",
                        min: { value: 1, message: "Rent must be at least $1" },
                        ...nonNegativeRule,
                    })}
                    onKeyDown={preventInvalidNumericInput}
                    onInput={(e) => {
                        if (e.target.value < 0) e.target.value = 0;
                    }}
                    placeholder="$/month"
                    aria-invalid={!!errors.rent}
                />
                {errors.rent && <p className={styles.fieldError}>{errors.rent.message}</p>}
            </div>

            <div className="input-group">
                <label htmlFor="address1">Address</label>
                <input
                    id="address1"
                    {...register("addressLine1", {
                        required: "Street Address is required",
                        maxLength: { value: 100, message: "Address cannot exceed 100 characters" },
                    })}
                    placeholder="Street Address"
                    aria-invalid={!!errors.addressLine1}
                />
               
            </div>
            <div className="input-group">
                 <input
                    id="address2"
                    {...register("addressLine2", {
                        maxLength: { value: 100, message: "Address cannot exceed 100 characters" },
                    })}
                    placeholder="Apt/Suite"
                    aria-invalid={!!errors.addressLine2}
                />
                {errors.addressLine1 && (
                    <p className={styles.fieldError}>{errors.addressLine1.message}</p>
                )}
                {errors.addressLine2 && (
                    <p className={styles.fieldError}>{errors.addressLine2.message}</p>
                )}
            </div>

            <div className="input-group">
                <label htmlFor="images">Upload Photo</label>
                <input
                    type="file"
                    id="images"
                    accept=".jpg,.jpeg,.png"
                    multiple
                    onChange={handleFileChange}
                />

                {selectedImages.length > 0 && (
                    <ul className={styles.fileList}>
                        {selectedImages.map((file, idx) => (
                            <li key={idx} className={styles.fileItem}>
                                <span>{file.name}</span>
                                <button
                                    type="button"
                                    className={styles.removeButton}
                                    onClick={() =>
                                        setSelectedImages((prev) => prev.filter((_, i) => i !== idx))
                                    }
                                >
                                    Remove
                                </button>
                            </li>
                        ))}
                    </ul>
                )}

                {errors.images && <p className={styles.fieldError}>{errors.images.message}</p>}
            </div>


            <div className={`${styles.formGroup} ${styles.utilitiesGroup}`}>
                <label>Utilities/Amenities Included</label>
                <div>
                    <div>
                        <input type="checkbox" id="water" value="water" {...register("utilities")} />
                        <label htmlFor="water" className={styles.checkboxLabel}>
                            Water
                        </label>
                    </div>

                    <div>
                        <input
                            type="checkbox"
                            id="parking"
                            value="parking"
                            {...register("utilities")}
                        />
                        <label htmlFor="parking" className={styles.checkboxLabel}>
                            Parking
                        </label>
                    </div>

                    <div>
                        <input
                            type="checkbox"
                            id="electricity"
                            value="electricity"
                            {...register("utilities")}
                        />
                        <label htmlFor="electricity" className={styles.checkboxLabel}>
                            Electricity
                        </label>
                    </div>

                    <div>
                        <input type="checkbox" id="gas" value="gas" {...register("utilities")} />
                        <label htmlFor="gas" className={styles.checkboxLabel}>
                            Gas
                        </label>
                    </div>

                    <div>
                        <input
                            type="checkbox"
                            id="laundry"
                            value="laundry"
                            {...register("utilities")}
                        />
                        <label htmlFor="laundry" className={styles.checkboxLabel}>
                            Laundry
                        </label>
                    </div>

                    <div>
                        <input type="checkbox" id="trash" value="trash" {...register("utilities")} />
                        <label htmlFor="trash" className={styles.checkboxLabel}>
                            Trash Collection
                        </label>
                    </div>
                </div>
            </div>

            {/* Group Bedrooms, Bathrooms, and Size into two rows */}
            <div className={styles.row}>
                <div className={`${styles.half} input-group`}>
                    <label htmlFor="bedrooms"># Bedrooms</label>
                    <input
                        type="number"
                        id="bedrooms"
                        {...register("bedrooms", {
                            required: "# Bedrooms is required",
                            min: { value: 1, message: "# Bedrooms must be at least 1" },
                            ...nonNegativeRule,
                        })}
                        onKeyDown={preventInvalidNumericInput}
                        onInput={(e) => {
                            if (e.target.value < 0) e.target.value = 0;
                        }}
                        placeholder="# Bedrooms"
                        aria-invalid={!!errors.bedrooms}
                    />

                    {errors.bedrooms && (
                        <p className={styles.fieldError}>{errors.bedrooms.message}</p>
                    )}
                </div>

                <div className={`${styles.half} input-group`}>
                    <label htmlFor="bathrooms"># Bathrooms</label>
                    <input
                        type="number"
                        id="bathrooms"
                        {...register("bathrooms", {
                            required: "# Bathrooms is required",
                            min: { value: 1, message: "# Bathrooms must be at least 1" },
                            ...nonNegativeRule,
                        })}
                        onKeyDown={preventInvalidNumericInput}
                        onInput={(e) => {
                            if (e.target.value < 0) e.target.value = 0;
                        }}
                        placeholder="# Bathrooms"
                        aria-invalid={!!errors.bathrooms}
                    />
                    {errors.bathrooms && (
                        <p className={styles.fieldError}>{errors.bathrooms.message}</p>
                    )}
                </div>
            </div>

            <div className={styles.row}>
                <div className={`${styles.half} input-group`}>
                    <label htmlFor="square_foot">Size (sqft)</label>
                    <input
                        type="number"
                        id="square_foot"
                        {...register("square_foot", {
                            required: "Size is required",
                            min: { value: 1, message: "Size must be at least 1 sqft" },
                            ...nonNegativeRule,
                        })}
                        onKeyDown={preventInvalidNumericInput}
                        onInput={(e) => {
                            if (e.target.value < 0) e.target.value = 0;
                        }}
                        placeholder="Size in sqft"
                        aria-invalid={!!errors.square_foot}
                    />
                    {errors.square_foot && (
                        <p className={styles.fieldError}>{errors.square_foot.message}</p>
                    )}
                </div>
                <div className={styles.half}>
                    {/* Empty half for alignment */}
                </div>
            </div>
            
            <div className="input-group">
                <label htmlFor="description">Description</label>
                <textarea
                    id="description"
                    {...register("description", {
                        required: "Description is required",
                        maxLength: { value: 1000, message: "Description cannot exceed 1000 characters" },
                    })}
                    placeholder="Describe the neighborhood, commute, roommates, utilities, etc."
                    aria-invalid={!!errors.description}
                    className={styles.descriptionTextarea}
                    rows={8}
                    style={{ resize: "vertical" }}
                />
                {errors.description && (
                    <p className={styles.fieldError}>{errors.description.message}</p>
                )}
            </div>

            <div className="input-group">
                <TagsInput control={control} name="tags" label="Tags (e.g., vegetarian, dog, cat)" />
            </div>

            <div className="input-group">
                <Controller
                    name="roommates"
                    control={control}
                    render={({ field }) => (
                        <RoommateSelector value={field.value || []} onChange={field.onChange} />
                    )}
                />
            </div>

            {/* This div is now a custom group for the rating/comments */}
            <div className={styles.ratingGroup}>
                <Controller
                    name="landlord_rating"
                    control={control}
                    rules={{
                        required: "Roomates rating is required",
                        validate: (v) => v > 0 || "Roomates rating is required",
                    }}
                    render={({ field }) => (
                        <div>
                            <label>Roomates Rating</label>
                            <br />
                            <Rating
                                emptySymbol={<span style={{ fontSize: "2rem", color: "#ccc" }}>☆</span>}
                                fullSymbol={<span style={{ fontSize: "2rem", color: "#EC221F" }}>★</span>}
                                initialRating={field.value}
                                onClick={(rate) => field.onChange(rate)}
                            />
                            {errors.landlord_rating && (
                                <p className={styles.fieldError}>{errors.landlord_rating.message}</p>
                            )}
                        </div>
                    )}
                />

                <div className="input-group">
                    <label htmlFor="comments">Roommate Comments</label>
                    <textarea
                        id="comments"
                        {...register("comments", {
                            maxLength: { value: 500, message: "Comments cannot exceed 500 characters" },
                        })}
                        placeholder="Tell us what your roommates are like (lifestyle, cleanliness, noise habits, schedules, personalities, etc.)"
                        aria-invalid={!!errors.comments}
                        className={styles.commentsTextarea}
                        rows={4}
                        style={{ resize: "vertical" }}
                    />
                    {errors.comments && (
                        <p className={styles.fieldError}>{errors.comments.message}</p>
                    )}
                </div>
            </div>

            <div className={styles.formActions}>
                <button type="submit">Submit</button>
            </div>

            {flash.text && (
                <div
                    className={flash.type === "success" ? styles.alertSuccess : styles.alertError}
                    role="status"
                    aria-live="polite"
                >
                    {flash.text}
                </div>
            )}

        </form>
    );
}